// src/pages/Contact.tsx
import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    gdprConsent: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.gdprConsent) {
      toast({
        title: "Napaka pri oddaji",
        description: "Prosimo, da potrdite soglasje za obdelavo osebnih podatkov.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('Form submitted:', formData);
    // In a real app, you would send this data to your backend or email service
    toast({
      title: "Sporočilo poslano",
      description: "Hvala za vaše sporočilo. Odgovorili vam bomo v najkrajšem možnem času.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      gdprConsent: false
    });
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="page-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/images/kontakt/avtoservis-kontakt.jpg')" }}
        ></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="hero-content max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Kontakt</h1>
            <p className="text-xl">Stopite v stik z nami. Z veseljem vam bomo pomagali.</p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Kontaktne informacije</h2>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2">
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow h-[720px] flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="flex items-start p-4 bg-gray-100 rounded-xl transition-all duration-300 hover:shadow-md hover:bg-blue-50">
                    <MapPin className="contact-icon mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold text-lg mb-1">Naslov</h3>
                      <p>Bukovec 10A, 2314 Zgornja Polskava</p>
                    </div>
                  </div>
                  <div className="flex items-start p-4 bg-gray-100 rounded-xl transition-all duration-300 hover:shadow-md hover:bg-blue-50">
                    <Phone className="contact-icon mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold text-lg mb-1">Telefon</h3>
                      <a href="tel:041273738" className="hover:text-autoBlue transition-colors">041 273 738</a>
                    </div>
                  </div>
                  <div className="flex items-start p-4 bg-gray-100 rounded-xl transition-all duration-300 hover:shadow-md hover:bg-blue-50">
                    <Mail className="contact-icon mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold text-lg mb-1">E-pošta</h3>
                      <a href="mailto:milan_acko@t-2.net" className="hover:text-autoBlue transition-colors">milan_acko@t-2.net</a>
                    </div>
                  </div>
                  <div className="flex items-start p-4 bg-gray-100 rounded-xl transition-all duration-300 hover:shadow-md hover:bg-blue-50">
                    <Clock className="contact-icon mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold text-lg mb-1">Delovni čas</h3>
                      <p>Ponedeljek - Petek: 8:00 - 17:00</p>
                      <p>Sobota: 8:00 - 12:00</p>
                      <p>Nedelja: Zaprto</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex justify-center">
                  <a 
                    href="https://www.google.com/maps/dir//Bukovec+10A,+2314+Zgornja+Polskava" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center group"
                  >
                    Navodila za pot
                    <MapPin className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2">
              <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue card-glow h-[720px]">
                <h2 className="text-2xl font-bold mb-6">Pošljite nam sporočilo</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Ime in priimek *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="form-input" 
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">E-pošta *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="form-input" 
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Telefon</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      className="form-input" 
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Sporočilo *</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows={5} 
                      required 
                      className="form-input" 
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="gdprConsent"
                        name="gdprConsent"
                        className="mt-1"
                        checked={formData.gdprConsent}
                        onChange={handleCheckboxChange}
                        required
                      />
                      <label htmlFor="gdprConsent" className="text-sm text-gray-700">
                        Z oddajo obrazca soglašam, da Avtoservis Vodnik kot upravljavec osebnih podatkov obdeluje moje osebne podatke, ki sem jih navedel/-a v tem obrazcu, za namen priprave in posredovanja ponudbe ter morebitne nadaljnje komunikacije v zvezi z mojo zahtevo. *
                        <br />
                        <Link to="/gdpr" className="text-autoBlue hover:underline">Za več informacij si oglejte našo GDPR politiko.</Link>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex justify-center">
                    <button type="submit" className="btn-primary inline-flex items-center group">
                      Pošlji
                      <Send className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-8 after:left-1/2 after:-translate-x-1/2">Kje nas najdete</h2>
          <div className="rounded-xl overflow-hidden shadow-xl transition-all duration-500 hover:shadow-2xl h-[400px] card-glow">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2750.4320073475104!2d15.5700746!3d46.42285!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476f7770ab6a1ee3%3A0xf5a6e9bf6df80ed2!2sBukovec%2010A%2C%202314%20Zgornja%20Polskava%2C%20Slovenia!5e0!3m2!1sen!2sus!4v1684162282828!5m2!1sen!2sus" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;